import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, foundersMessageApi, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const ACCENT_COLOR_PRESETS = [
  { label: 'Deep Purple (Pooja)', value: '#7c4bb9' },
  { label: 'Lavender Purple (Sneha)', value: '#9072c8' },
  { label: 'Royal Indigo', value: '#6366f1' },
  { label: 'Ocean Blue', value: '#0ea5e9' },
  { label: 'Emerald Green', value: '#10b981' },
  { label: 'Rose Pink', value: '#ec4899' },
  { label: 'Amber Gold', value: '#f59e0b' },
];

function AdminFoundersMessagePage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [activeTab, setActiveTab] = useState('messages'); // 'messages', 'settings', 'preview'
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0 });

  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order');

  // Section Settings State
  const [settings, setSettings] = useState({
    badgeText: 'PSYCHMITRA',
    titlePrefix: 'Founderʼs',
    titleAccent: 'Message',
    photoImage: '/assets/images/team/team-details-img-1.jpg',
    photoAlt: 'PsychMitra Co-Founders - Pooja Jagyasi & Sneha Nandanwar',
    photoBadgeNames: 'Pooja Jagyasi & Sneha Nandanwar',
    photoBadgeRole: 'Psychologists & Co-Founders',
    isEnabled: true,
  });

  // Photo Upload State
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Founder Message Modal State (Create / Edit)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    role: 'Psychologist | Co-Founder',
    quote: '',
    secondaryQuote: '',
    accentColor: '#7c4bb9',
    order: 1,
    isPublished: true,
  });

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });

  // Reset Confirmation Modal State
  const [resetModal, setResetModal] = useState({ isOpen: false, loading: false });

  // Preview Item Modal
  const [previewItem, setPreviewItem] = useState(null);

  // Toast Feedback State
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4500);
  };

  // Fetch Data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await foundersMessageApi.adminGetFoundersMessages({
        search: searchQuery,
        status: statusFilter,
        sortBy,
      });

      if (res?.success && res.data) {
        setMessages(res.data.messages || []);
        if (res.data.settings) {
          setSettings(res.data.settings);
        }
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      }
    } catch (err) {
      console.error('Error loading founder messages:', err);
      showFeedback('error', err.message || 'Failed to load founder messages');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, sortBy]);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchData();
  }, [fetchData, authSession?.token, navigate]);

  // Open Create Modal
  const handleOpenCreate = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      name: '',
      role: 'Psychologist | Co-Founder',
      quote: '',
      secondaryQuote: '',
      accentColor: ACCENT_COLOR_PRESETS[messages.length % ACCENT_COLOR_PRESETS.length].value,
      order: messages.length + 1,
      isPublished: true,
    });
    setModalError('');
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (item) => {
    setModalMode('edit');
    setEditingId(item._id);
    setFormData({
      name: item.name || '',
      role: item.role || 'Psychologist | Co-Founder',
      quote: item.quote || '',
      secondaryQuote: item.secondaryQuote || '',
      accentColor: item.accentColor || '#7c4bb9',
      order: item.order || 1,
      isPublished: item.isPublished !== false,
    });
    setModalError('');
    setIsModalOpen(true);
  };

  // Save Message Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setModalError('Please provide the founder\'s name.');
      return;
    }
    if (!formData.quote.trim()) {
      setModalError('Please provide the founder\'s main quote message.');
      return;
    }

    try {
      setFormSubmitting(true);
      setModalError('');

      if (modalMode === 'create') {
        const res = await foundersMessageApi.adminCreateFounderMessage(formData);
        if (res?.success) {
          showFeedback('success', `Founder message for "${formData.name}" added successfully!`);
          setIsModalOpen(false);
          fetchData();
        }
      } else {
        const res = await foundersMessageApi.adminUpdateFounderMessage(editingId, formData);
        if (res?.success) {
          showFeedback('success', `Founder message for "${formData.name}" updated successfully!`);
          setIsModalOpen(false);
          fetchData();
        }
      }
    } catch (err) {
      console.error('Error saving founder message:', err);
      setModalError(err.message || 'Failed to save founder message.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Toggle Publish Status
  const handleTogglePublish = async (item) => {
    try {
      const res = await foundersMessageApi.adminTogglePublish(item._id);
      if (res?.success) {
        showFeedback('success', `Message status updated to ${res.data?.isPublished ? 'Published' : 'Draft'}`);
        setMessages((prev) =>
          prev.map((m) => (m._id === item._id ? { ...m, isPublished: !m.isPublished } : m))
        );
        setStats((prev) => ({
          ...prev,
          published: item.isPublished ? prev.published - 1 : prev.published + 1,
          drafts: item.isPublished ? prev.drafts + 1 : prev.drafts - 1,
        }));
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      showFeedback('error', err.message || 'Failed to toggle status');
    }
  };

  // Delete Item
  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      const res = await foundersMessageApi.adminDeleteFounderMessage(deleteModal.item._id);
      if (res?.success) {
        showFeedback('success', 'Founder message deleted successfully.');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        fetchData();
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      showFeedback('error', err.message || 'Failed to delete message');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Save Settings
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      setSavingSettings(true);
      const res = await foundersMessageApi.adminUpdateSettings(settings);
      if (res?.success) {
        setSettings(res.data);
        showFeedback('success', 'Founder section settings updated and published live!');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      showFeedback('error', err.message || 'Failed to save section settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle Photo Upload
  const handlePhotoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file (JPG, PNG, WebP).');
      return;
    }

    try {
      setUploadingPhoto(true);
      setUploadError('');
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const res = await foundersMessageApi.uploadPhoto(uploadFormData);
      if (res?.success && res.url) {
        setSettings((prev) => ({ ...prev, photoImage: res.url }));
        showFeedback('success', 'Co-Founders photo uploaded! Click "Save Settings" to publish.');
      } else {
        setUploadError(res?.message || 'Failed to upload photo');
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      setUploadError(err.message || 'Failed to upload photo file');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Handle Reset to Defaults
  const handleResetConfirm = async () => {
    try {
      setResetModal((prev) => ({ ...prev, loading: true }));
      const res = await foundersMessageApi.adminResetFoundersData();
      if (res?.success) {
        showFeedback('success', 'Founder section and default messages reset successfully!');
        setResetModal({ isOpen: false, loading: false });
        fetchData();
      }
    } catch (err) {
      console.error('Reset error:', err);
      showFeedback('error', err.message || 'Failed to reset founder data');
      setResetModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const photoPreviewUrl = settings.photoImage ? getMediaUrl(settings.photoImage) : '/assets/images/team/team-details-img-1.jpg';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', color: '#1e293b' }}>
      {/* Fixed Sidebar */}
      <DashboardSidebar userRole="admin" />

      {/* Main Content Area with proper 280px sidebar offset */}
      <div className="sidebar-content-wrapper" style={{ flex: 1, padding: '32px 40px', minHeight: '100vh', width: 'calc(100% - 280px)', maxWidth: '100%' }}>
        {/* Toast Feedback Notification */}
        {feedback.message && (
          <div
            style={{
              position: 'fixed',
              top: '25px',
              right: '25px',
              zIndex: 99999,
              padding: '14px 24px',
              borderRadius: '12px',
              color: '#fff',
              background: feedback.type === 'error' ? '#ef4444' : '#10b981',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
              fontWeight: '700',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <span>{feedback.type === 'error' ? '⚠️' : '✅'}</span>
            <span>{feedback.message}</span>
          </div>
        )}

        {/* Top Header Card */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '26px 32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            border: '1px solid #ede8f5',
            marginBottom: '26px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '30px' }}>💬</span>
                <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '800', color: '#1e1335', letterSpacing: '-0.5px' }}>
                  Founderʼs Message Management
                </h1>
              </div>
              <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14.5px' }}>
                Manage homepage founder quotes, Co-Founders portrait card, badges, and section styling.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <a
                href="/#founders-message"
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  textDecoration: 'none',
                  padding: '11px 18px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '13.5px',
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  transition: 'all 0.2s',
                }}
              >
                <span>👁️</span> View on Homepage
              </a>

              <button
                type="button"
                onClick={() => setResetModal({ isOpen: true, loading: false })}
                style={{
                  padding: '11px 18px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '13.5px',
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  color: '#b91c1c',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                🔄 Reset to Defaults
              </button>

              <button
                type="button"
                onClick={handleOpenCreate}
                style={{
                  background: 'linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%)',
                  color: '#fff',
                  border: 'none',
                  padding: '11px 22px',
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(124, 75, 185, 0.35)',
                  transition: 'all 0.2s',
                }}
              >
                <span>➕</span> Add Founder Message
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', borderBottom: '1.5px solid #ede8f5', paddingBottom: '2px' }}>
            <button
              onClick={() => setActiveTab('messages')}
              style={{
                background: 'none',
                border: 'none',
                padding: '11px 20px',
                fontSize: '14.5px',
                fontWeight: activeTab === 'messages' ? '700' : '600',
                color: activeTab === 'messages' ? '#7c4bb9' : '#64748b',
                borderBottom: activeTab === 'messages' ? '3px solid #7c4bb9' : '3px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
              }}
            >
              <span>📑</span> Founder Messages ({messages.length})
            </button>

            <button
              onClick={() => setActiveTab('settings')}
              style={{
                background: 'none',
                border: 'none',
                padding: '11px 20px',
                fontSize: '14.5px',
                fontWeight: activeTab === 'settings' ? '700' : '600',
                color: activeTab === 'settings' ? '#7c4bb9' : '#64748b',
                borderBottom: activeTab === 'settings' ? '3px solid #7c4bb9' : '3px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
              }}
            >
              <span>⚙️</span> Section &amp; Co-Founders Photo
            </button>

            <button
              onClick={() => setActiveTab('preview')}
              style={{
                background: 'none',
                border: 'none',
                padding: '11px 20px',
                fontSize: '14.5px',
                fontWeight: activeTab === 'preview' ? '700' : '600',
                color: activeTab === 'preview' ? '#7c4bb9' : '#64748b',
                borderBottom: activeTab === 'preview' ? '3px solid #7c4bb9' : '3px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
              }}
            >
              <span>🎨</span> Live Section Simulation
            </button>
          </div>
        </div>

        {/* =========================================================
            TAB 1: FOUNDER MESSAGES LIST (CRUD)
            ========================================================= */}
        {activeTab === 'messages' && (
          <>
            {/* Quick Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '24px' }}>
              <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '18px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #ede8f5' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>TOTAL QUOTES</span>
                <h3 style={{ margin: '8px 0 0', fontSize: '28px', fontWeight: '800', color: '#1e1335' }}>{stats.total}</h3>
              </div>

              <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '18px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #ede8f5' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.8px' }}>PUBLISHED &amp; LIVE</span>
                <h3 style={{ margin: '8px 0 0', fontSize: '28px', fontWeight: '800', color: '#10b981' }}>{stats.published}</h3>
              </div>

              <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '18px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #ede8f5' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>DRAFTS / HIDDEN</span>
                <h3 style={{ margin: '8px 0 0', fontSize: '28px', fontWeight: '800', color: '#f59e0b' }}>{stats.drafts}</h3>
              </div>

              <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '18px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #ede8f5' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#7c4bb9', textTransform: 'uppercase', letterSpacing: '0.8px' }}>SECTION STATUS</span>
                <h3 style={{ margin: '8px 0 0', fontSize: '20px', fontWeight: '800', color: settings.isEnabled ? '#10b981' : '#ef4444' }}>
                  {settings.isEnabled ? '✅ Enabled' : '⏸️ Disabled'}
                </h3>
              </div>
            </div>

            {/* Filters and Search Bar */}
            <div style={{ background: '#fff', padding: '18px 24px', borderRadius: '18px', marginBottom: '22px', border: '1px solid #ede8f5', display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '280px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
                  <input
                    type="text"
                    placeholder="Search by name, role, or quote content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '11px 14px 11px 38px',
                      borderRadius: '12px',
                      border: '1.5px solid #e2e8f0',
                      fontSize: '14px',
                      background: '#f8fafc',
                    }}
                  />
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '11px 16px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    fontSize: '14px',
                    background: '#f8fafc',
                    color: '#334155',
                    fontWeight: '600',
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="published">Published Only</option>
                  <option value="draft">Drafts Only</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: '11px 16px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    fontSize: '14px',
                    background: '#f8fafc',
                    color: '#334155',
                    fontWeight: '600',
                  }}
                >
                  <option value="order">Sort by Order</option>
                  <option value="name_asc">Name (A-Z)</option>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>

              <div style={{ fontSize: '13.5px', color: '#64748b', fontWeight: 600 }}>
                Showing {messages.length} quote{messages.length === 1 ? '' : 's'}
              </div>
            </div>

            {/* Messages List Cards */}
            {loading ? (
              <div style={{ background: '#fff', padding: '60px 20px', textAlign: 'center', borderRadius: '18px', border: '1px solid #ede8f5' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
                <p style={{ margin: 0, color: '#64748b', fontWeight: 600 }}>Loading Founder Messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div style={{ background: '#fff', padding: '60px 20px', textAlign: 'center', borderRadius: '18px', border: '1px solid #ede8f5' }}>
                <div style={{ fontSize: '44px', marginBottom: '12px' }}>💬</div>
                <h3 style={{ margin: '0 0 8px', color: '#1e1335', fontSize: '20px' }}>No Founder Messages Found</h3>
                <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14.5px' }}>
                  {searchQuery ? 'Try adjusting your search query or filters.' : 'Get started by adding your first founder quote.'}
                </p>
                <button
                  type="button"
                  onClick={handleOpenCreate}
                  style={{
                    background: '#7c4bb9',
                    color: '#fff',
                    padding: '11px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  ➕ Add Founder Message
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {messages.map((item, index) => {
                  const accent = item.accentColor || '#7c4bb9';
                  return (
                    <div
                      key={item._id}
                      style={{
                        background: '#fff',
                        borderRadius: '18px',
                        padding: '24px 28px',
                        boxShadow: '0 4px 18px rgba(0,0,0,0.03)',
                        border: '1.5px solid #ede8f5',
                        borderLeft: `6px solid ${accent}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      }}
                    >
                      {/* Top Row: Founder Info & Action Buttons */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div
                            style={{
                              width: '46px',
                              height: '46px',
                              borderRadius: '14px',
                              background: `${accent}18`,
                              color: accent,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              fontSize: '17px',
                            }}
                          >
                            #{item.order || index + 1}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#1e1335' }}>
                                {item.name}
                              </h3>
                              <span
                                style={{
                                  fontSize: '11.5px',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  padding: '3px 12px',
                                  borderRadius: '20px',
                                  background: item.isPublished ? '#dcfce7' : '#fef3c7',
                                  color: item.isPublished ? '#15803d' : '#b45309',
                                }}
                              >
                                {item.isPublished ? '● Published' : '○ Draft'}
                              </span>
                            </div>
                            <p style={{ margin: '4px 0 0', fontSize: '13.5px', color: accent, fontWeight: 700 }}>
                              {item.role}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => setPreviewItem(item)}
                            title="Preview Card"
                            style={{
                              background: '#f8fafc',
                              border: '1px solid #cbd5e1',
                              color: '#334155',
                              padding: '8px 14px',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            👁️ Preview
                          </button>

                          <button
                            type="button"
                            onClick={() => handleTogglePublish(item)}
                            title={item.isPublished ? 'Unpublish quote' : 'Publish quote live'}
                            style={{
                              background: item.isPublished ? '#fef3c7' : '#dcfce7',
                              border: 'none',
                              color: item.isPublished ? '#92400e' : '#166534',
                              padding: '8px 14px',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {item.isPublished ? '⏸️ Unpublish' : '🚀 Publish'}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit Quote"
                            style={{
                              background: '#f3e8ff',
                              border: 'none',
                              color: '#7c4bb9',
                              padding: '8px 16px',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => setDeleteModal({ isOpen: true, item, loading: false })}
                            title="Delete Quote"
                            style={{
                              background: '#fee2e2',
                              border: 'none',
                              color: '#dc2626',
                              padding: '8px 14px',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>

                      {/* Quote Text Snippet */}
                      <div
                        style={{
                          background: '#faf7fd',
                          padding: '16px 20px',
                          borderRadius: '14px',
                          border: '1px solid #f1e9fa',
                          color: '#475569',
                          fontSize: '14.5px',
                          lineHeight: 1.65,
                        }}
                      >
                        <p style={{ margin: 0, fontStyle: 'italic' }}>
                          "{item.quote}"
                        </p>
                        {item.secondaryQuote && (
                          <p style={{ margin: '10px 0 0', fontStyle: 'italic', color: '#64748b' }}>
                            "{item.secondaryQuote}"
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* =========================================================
            TAB 2: SECTION SETTINGS & CO-FOUNDERS PHOTO
            ========================================================= */}
        {activeTab === 'settings' && (
          <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #ede8f5' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '26px', flexWrap: 'wrap', gap: '14px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1e1335' }}>
                  Homepage Founder Section Configuration
                </h2>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14.5px' }}>
                  Customize the section header, badges, titles, and upload the Co-Founders group photo.
                </p>
              </div>

              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={savingSettings}
                style={{
                  background: 'linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 28px',
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: '14.5px',
                  cursor: savingSettings ? 'not-allowed' : 'pointer',
                  opacity: savingSettings ? 0.7 : 1,
                  boxShadow: '0 4px 15px rgba(124, 75, 185, 0.35)',
                }}
              >
                {savingSettings ? '💾 Saving Changes...' : '💾 Save & Publish Settings'}
              </button>
            </div>

            <form onSubmit={handleSaveSettings}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '26px' }}>
                {/* Left Column: Headers & Badge Configuration */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ background: '#faf8fd', padding: '18px 22px', borderRadius: '16px', border: '1.5px solid #ede8f5' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: 700, color: '#1e1335', fontSize: '15px' }}>
                      <input
                        type="checkbox"
                        checked={settings.isEnabled}
                        onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
                        style={{ width: '20px', height: '20px', accentColor: '#7c4bb9', cursor: 'pointer' }}
                      />
                      <span>Enable Founderʼs Message Section on Homepage</span>
                    </label>
                    <p style={{ margin: '6px 0 0 32px', fontSize: '13px', color: '#64748b' }}>
                      When enabled, this section will be rendered live for all visitors on the homepage.
                    </p>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                      Top Badge Text
                    </label>
                    <input
                      type="text"
                      value={settings.badgeText}
                      onChange={(e) => setSettings({ ...settings, badgeText: e.target.value })}
                      placeholder="e.g. PSYCHMITRA"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1.5px solid #e2e8f0',
                        fontSize: '14.5px',
                        background: '#f8fafc',
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                        Title Prefix
                      </label>
                      <input
                        type="text"
                        value={settings.titlePrefix}
                        onChange={(e) => setSettings({ ...settings, titlePrefix: e.target.value })}
                        placeholder="e.g. Founderʼs"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: '1.5px solid #e2e8f0',
                          fontSize: '14.5px',
                          background: '#f8fafc',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                        Title Accent / Highlight
                      </label>
                      <input
                        type="text"
                        value={settings.titleAccent}
                        onChange={(e) => setSettings({ ...settings, titleAccent: e.target.value })}
                        placeholder="e.g. Message"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: '1.5px solid #e2e8f0',
                          fontSize: '14.5px',
                          background: '#f8fafc',
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                      Co-Founders Overlay Badge Names
                    </label>
                    <input
                      type="text"
                      value={settings.photoBadgeNames}
                      onChange={(e) => setSettings({ ...settings, photoBadgeNames: e.target.value })}
                      placeholder="e.g. Pooja Jagyasi & Sneha Nandanwar"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1.5px solid #e2e8f0',
                        fontSize: '14.5px',
                        background: '#f8fafc',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                      Co-Founders Overlay Badge Role / Subtitle
                    </label>
                    <input
                      type="text"
                      value={settings.photoBadgeRole}
                      onChange={(e) => setSettings({ ...settings, photoBadgeRole: e.target.value })}
                      placeholder="e.g. Psychologists & Co-Founders"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1.5px solid #e2e8f0',
                        fontSize: '14.5px',
                        background: '#f8fafc',
                      }}
                    />
                  </div>
                </div>

                {/* Right Column: Photo Upload & Image Card */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                      Co-Founders Portrait Image
                    </label>

                    {/* Image Preview Box */}
                    <div
                      style={{
                        position: 'relative',
                        borderRadius: '22px',
                        overflow: 'hidden',
                        border: '4px solid #ede8f5',
                        background: '#f8fafc',
                        maxHeight: '340px',
                        boxShadow: '0 10px 30px rgba(124, 75, 185, 0.12)',
                        marginBottom: '16px',
                      }}
                    >
                      <img
                        src={photoPreviewUrl}
                        alt={settings.photoAlt}
                        style={{ width: '100%', height: '100%', maxHeight: '330px', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/team/team-details-img-1.jpg';
                        }}
                      />

                      {/* Overlay badge preview */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '14px',
                          left: '14px',
                          right: '14px',
                          background: 'rgba(255,255,255,0.96)',
                          padding: '12px 16px',
                          borderRadius: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: '#241242' }}>
                            {settings.photoBadgeNames}
                          </div>
                          <div style={{ fontSize: '12px', color: '#7c4bb9', fontWeight: 700 }}>
                            {settings.photoBadgeRole}
                          </div>
                        </div>
                        <span style={{ color: '#7c4bb9', fontSize: '16px' }}>❤️</span>
                      </div>
                    </div>

                    {/* File Upload Controls */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label
                        style={{
                          background: '#f3e8ff',
                          color: '#7c4bb9',
                          padding: '11px 20px',
                          borderRadius: '12px',
                          fontWeight: 700,
                          fontSize: '13.5px',
                          cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                        }}
                      >
                        <span>📷</span>
                        <span>{uploadingPhoto ? 'Uploading Photo...' : 'Upload New Photo'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoFileChange}
                          disabled={uploadingPhoto}
                          style={{ display: 'none' }}
                        />
                      </label>

                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, photoImage: '/assets/images/team/team-details-img-1.jpg' })}
                        style={{
                          background: '#f8fafc',
                          border: '1.5px solid #cbd5e1',
                          color: '#64748b',
                          padding: '10px 16px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Use Default Photo
                      </button>
                    </div>

                    {uploadError && (
                      <p style={{ margin: '8px 0 0', color: '#ef4444', fontSize: '13px', fontWeight: 600 }}>
                        ⚠️ {uploadError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                      Photo Alt Description
                    </label>
                    <input
                      type="text"
                      value={settings.photoAlt}
                      onChange={(e) => setSettings({ ...settings, photoAlt: e.target.value })}
                      placeholder="e.g. PsychMitra Co-Founders - Pooja Jagyasi & Sneha Nandanwar"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1.5px solid #e2e8f0',
                        fontSize: '14.5px',
                        background: '#f8fafc',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '30px', paddingTop: '22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={savingSettings}
                  style={{
                    background: 'linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%)',
                    color: '#fff',
                    border: 'none',
                    padding: '13px 32px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '15px',
                    cursor: savingSettings ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 18px rgba(124, 75, 185, 0.35)',
                  }}
                >
                  {savingSettings ? '💾 Saving Changes...' : '💾 Save & Publish Settings'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* =========================================================
            TAB 3: LIVE SECTION SIMULATION
            ========================================================= */}
        {activeTab === 'preview' && (
          <div style={{ background: '#fcfaff', borderRadius: '24px', padding: '44px 32px', border: '2px dashed #d8b4fe' }}>
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#f0e9ff',
                  color: '#7c4bb9',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  padding: '7px 22px',
                  borderRadius: '30px',
                }}
              >
                ● {settings.badgeText || 'PSYCHMITRA'}
              </span>
              <h2 style={{ margin: '12px 0 0', fontSize: '34px', fontWeight: 800, color: '#1e1335' }}>
                {settings.titlePrefix || 'Founderʼs'}{' '}
                <span style={{ color: '#7c4bb9' }}>{settings.titleAccent || 'Message'}</span>
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center', maxWidth: '1100px', margin: '0 auto' }}>
              {/* Left Photo */}
              <div>
                <div
                  style={{
                    position: 'relative',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: '4px solid #fff',
                    boxShadow: '0 20px 45px rgba(124, 75, 185, 0.16)',
                  }}
                >
                  <img
                    src={photoPreviewUrl}
                    alt={settings.photoAlt}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      left: '16px',
                      right: '16px',
                      background: 'rgba(255, 255, 255, 0.94)',
                      padding: '14px 18px',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#241242' }}>
                        {settings.photoBadgeNames}
                      </div>
                      <div style={{ fontSize: '12.5px', color: '#7c4bb9', fontWeight: 700 }}>
                        {settings.photoBadgeRole}
                      </div>
                    </div>
                    <span style={{ color: '#7c4bb9', fontSize: '18px' }}>❤️</span>
                  </div>
                </div>
              </div>

              {/* Right Quotes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {messages
                  .filter((m) => m.isPublished)
                  .map((item, idx) => {
                    const borderAccent = item.accentColor || (idx % 2 === 0 ? '#7c4bb9' : '#9072c8');
                    return (
                      <div
                        key={item._id || idx}
                        style={{
                          background: '#fff',
                          borderRadius: '22px',
                          padding: '26px 30px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                          border: '1.5px solid #ede8f5',
                          borderLeft: `6px solid ${borderAccent}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#241242' }}>
                              {item.name}
                            </h4>
                            <span style={{ fontSize: '13.5px', color: borderAccent, fontWeight: 700 }}>
                              {item.role}
                            </span>
                          </div>
                          <span style={{ color: borderAccent, fontSize: '22px' }}>❝</span>
                        </div>
                        <p style={{ margin: '0 0 10px', fontSize: '14.5px', lineHeight: 1.72, color: '#555160' }}>
                          "{item.quote}"
                        </p>
                        {item.secondaryQuote && (
                          <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.72, color: '#555160' }}>
                            "{item.secondaryQuote}"
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* =========================================================
            CREATE / EDIT MODAL
            ========================================================= */}
        {isModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '680px',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '32px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1e1335' }}>
                  {modalMode === 'create' ? '➕ Add New Founder Message' : '✏️ Edit Founder Message'}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{ background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '50%', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              {modalError && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 18px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px', fontWeight: 600 }}>
                  ⚠️ {modalError}
                </div>
              )}

              <form onSubmit={handleSubmitForm} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '13.5px', color: '#334155' }}>
                      Founder Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Pooja Jagyasi"
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '14px', background: '#f8fafc' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '13.5px', color: '#334155' }}>
                      Role / Subtitle <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      placeholder="e.g. Psychologist | Co-Founder"
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '14px', background: '#f8fafc' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '13.5px', color: '#334155' }}>
                      Accent Theme Color
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                        style={{ width: '44px', height: '44px', padding: 0, border: 'none', borderRadius: '10px', cursor: 'pointer' }}
                      />
                      <select
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                        style={{ flex: 1, padding: '11px 12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '13.5px', background: '#f8fafc' }}
                      >
                        {ACCENT_COLOR_PRESETS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '13.5px', color: '#334155' }}>
                      Display Order
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '14px', background: '#f8fafc' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '13.5px', color: '#334155' }}>
                    Primary Quote / Message Paragraph <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={formData.quote}
                    onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                    placeholder="Enter the founder's main message or philosophy..."
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '14px', lineHeight: 1.6, background: '#f8fafc' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '13.5px', color: '#334155' }}>
                    Secondary Quote Paragraph <span style={{ color: '#64748b', fontWeight: 400 }}>(Optional second paragraph)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={formData.secondaryQuote}
                    onChange={(e) => setFormData({ ...formData, secondaryQuote: e.target.value })}
                    placeholder="Enter an optional second paragraph or concluding message..."
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '14px', lineHeight: 1.6, background: '#f8fafc' }}
                  />
                </div>

                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '14px', border: '1px solid #f1f5f9' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: 700, color: '#1e1335', fontSize: '14.5px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isPublished}
                      onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#7c4bb9', cursor: 'pointer' }}
                    />
                    <span>Publish this message immediately on Homepage</span>
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    style={{ background: '#f1f5f9', border: 'none', color: '#475569', padding: '12px 22px', borderRadius: '12px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    style={{
                      background: 'linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%)',
                      color: '#fff',
                      border: 'none',
                      padding: '12px 28px',
                      borderRadius: '12px',
                      fontWeight: 700,
                      cursor: formSubmitting ? 'not-allowed' : 'pointer',
                      opacity: formSubmitting ? 0.7 : 1,
                    }}
                  >
                    {formSubmitting ? 'Saving...' : modalMode === 'create' ? '➕ Create Message' : '💾 Update Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* =========================================================
            CARD PREVIEW MODAL
            ========================================================= */}
        {previewItem && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '620px',
                padding: '32px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#7c4bb9', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Homepage Quote Card Preview
                </span>
                <button
                  type="button"
                  onClick={() => setPreviewItem(null)}
                  style={{ background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '50%', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '22px',
                  padding: '30px',
                  boxShadow: '0 12px 35px rgba(124, 75, 185, 0.12)',
                  border: '1.5px solid #ede8f5',
                  borderLeft: `6px solid ${previewItem.accentColor || '#7c4bb9'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#241242' }}>
                      {previewItem.name}
                    </h4>
                    <span style={{ fontSize: '14px', color: previewItem.accentColor || '#7c4bb9', fontWeight: 700 }}>
                      {previewItem.role}
                    </span>
                  </div>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: `${previewItem.accentColor || '#7c4bb9'}18`,
                      color: previewItem.accentColor || '#7c4bb9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                    }}
                  >
                    ❝
                  </div>
                </div>

                <p style={{ fontSize: '15px', lineHeight: 1.72, color: '#555160', marginBottom: '12px' }}>
                  "{previewItem.quote}"
                </p>

                {previewItem.secondaryQuote && (
                  <p style={{ fontSize: '15px', lineHeight: 1.72, color: '#555160', margin: 0 }}>
                    "{previewItem.secondaryQuote}"
                  </p>
                )}
              </div>

              <div style={{ marginTop: '24px', textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => setPreviewItem(null)}
                  style={{ background: '#7c4bb9', color: '#fff', border: 'none', padding: '11px 26px', borderRadius: '12px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================
            DELETE CONFIRMATION MODAL
            ========================================================= */}
        {deleteModal.isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '460px',
                padding: '30px',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ width: '58px', height: '58px', borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', margin: '0 auto 16px' }}>
                🗑️
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 800, color: '#1e1335' }}>
                Delete Founder Message?
              </h3>
              <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '14.5px', lineHeight: 1.5 }}>
                Are you sure you want to delete the founder message for <strong>"{deleteModal.item?.name}"</strong>? This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', padding: '12px 24px', borderRadius: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteModal.loading}
                  style={{ background: '#dc2626', border: 'none', color: '#fff', padding: '12px 26px', borderRadius: '12px', fontWeight: 700, cursor: deleteModal.loading ? 'not-allowed' : 'pointer', opacity: deleteModal.loading ? 0.7 : 1 }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================
            RESET TO DEFAULTS MODAL
            ========================================================= */}
        {resetModal.isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '480px',
                padding: '30px',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ width: '58px', height: '58px', borderRadius: '50%', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', margin: '0 auto 16px' }}>
                🔄
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 800, color: '#1e1335' }}>
                Reset Founder Data to Default?
              </h3>
              <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '14.5px', lineHeight: 1.5 }}>
                This will reset all section settings and restore default founder quotes for Pooja Jagyasi and Sneha Nandanwar. Any custom quotes created will be overwritten.
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setResetModal({ isOpen: false, loading: false })}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', padding: '12px 24px', borderRadius: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResetConfirm}
                  disabled={resetModal.loading}
                  style={{ background: '#d97706', border: 'none', color: '#fff', padding: '12px 26px', borderRadius: '12px', fontWeight: 700, cursor: resetModal.loading ? 'not-allowed' : 'pointer', opacity: resetModal.loading ? 0.7 : 1 }}
                >
                  {resetModal.loading ? 'Resetting...' : 'Yes, Reset to Default'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminFoundersMessagePage;
